import { useState } from "react";

import {
  formatTime,
  heightByTwelfths,
  parseTime,
} from "@nav-games/tides";

/// Rule-of-twelfths tide-height calculator. Pure-arithmetic version
/// of the standard RYA Day Skipper calculation:
///
///   Hour 1 fall = 1/12 of range
///   Hour 2 fall = 2/12 of range  (cumulative 3/12)
///   Hour 3 fall = 3/12             (cumulative 6/12)
///   Hour 4 fall = 3/12             (cumulative 9/12)
///   Hour 5 fall = 2/12             (cumulative 11/12)
///   Hour 6 fall = 1/12             (LW reached)
///
/// Shown line-by-line so the student can verify on paper.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  hint,
  type = "text",
  step = 0.1,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  type?: string;
  step?: number;
}) {
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        flex: 1,
        minWidth: 120,
      }}
    >
      <span className="small muted">{label}</span>
      <input
        type={type}
        inputMode={type === "number" ? "decimal" : "text"}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function RuleOfTwelfthsCalculator() {
  const [hwHeight, setHwHeight] = useState("5.2");
  const [lwHeight, setLwHeight] = useState("1.6");
  const [hwTime, setHwTime] = useState("09:00");
  const [now, setNow] = useState("12:00");
  const [direction, setDirection] = useState<"ebb" | "flood">("ebb");
  const [cycleHours, setCycleHours] = useState("6");

  const hw = Number(hwHeight);
  const lw = Number(lwHeight);
  const cyc = Number(cycleHours);
  const hwMin = parseTime(hwTime);
  const nowMin = parseTime(now);
  const valid =
    Number.isFinite(hw) &&
    Number.isFinite(lw) &&
    Number.isFinite(cyc) &&
    cyc > 0 &&
    hwMin !== null &&
    nowMin !== null;

  const hoursFromHw = valid
    ? ((nowMin! - hwMin! + 1440) % 1440) / 60
    : 0;
  // For an ebb tide the student wants hours from HW (positive).
  // For a flood tide the calculator swaps HW <-> LW so the same
  // formula returns the rising height.
  const hwEff = direction === "ebb" ? hw : lw;
  const lwEff = direction === "ebb" ? lw : hw;
  const rangeM = Math.abs(hw - lw);
  const heightNow = valid
    ? heightByTwelfths(hwEff, lwEff, hoursFromHw, cyc)
    : null;

  // Build the cumulative-fallen-table for the working block.
  const cumFallen = [0, 1, 3, 6, 9, 11, 12]; // /12 of range
  const tForRound = Math.max(0, Math.min(6, hoursFromHw));
  const loIdx = Math.floor(tForRound);
  const hiIdx = Math.min(6, loIdx + 1);
  const frac = tForRound - loIdx;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Rule-of-twelfths calculator</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Quick tide-height estimate between HW and LW. Set HW + LW heights
        + the time of HW, then the time you care about. Pick &quot;ebb&quot;
        when the tide is falling (HW past, LW to come), &quot;flood&quot;
        when rising. The working is shown so you can check it on paper.
      </p>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {(["ebb", "flood"] as const).map((d) => (
          <button
            key={d}
            type="button"
            className={direction === d ? "" : "secondary"}
            onClick={() => setDirection(d)}
            style={{ flex: "1 1 100px" }}
            aria-pressed={direction === d}
          >
            {d === "ebb" ? "Ebbing (falling)" : "Flooding (rising)"}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field
          label="HW height (m)"
          value={hwHeight}
          onChange={setHwHeight}
          type="number"
        />
        <Field
          label="LW height (m)"
          value={lwHeight}
          onChange={setLwHeight}
          type="number"
        />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field
          label={direction === "ebb" ? "HW time (UT)" : "LW time (UT)"}
          value={hwTime}
          onChange={setHwTime}
          hint="HH:MM"
        />
        <Field
          label="Time of interest"
          value={now}
          onChange={setNow}
          hint="HH:MM"
        />
        <Field
          label="HW → LW duration (h)"
          value={cycleHours}
          onChange={setCycleHours}
          hint="Usually 6 h"
          type="number"
        />
      </div>

      {valid && heightNow !== null ? (
        <>
          <div
            style={{
              textAlign: "center",
              padding: "10px 4px",
              marginBottom: 12,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--accent)",
              background: "var(--accent-soft)",
            }}
          >
            <div className="small muted">Height at {formatTime(nowMin!)}</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {heightNow.toFixed(2)} m
            </div>
          </div>

          <div
            className="small"
            style={{
              padding: 12,
              background: "var(--bg-elev)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
{`Working
─────────────────────────────────────────────
HW height    = ${hw.toFixed(2)} m
LW height    = ${lw.toFixed(2)} m
Range        = ${rangeM.toFixed(2)} m   (HW − LW)
HW time      = ${formatTime(hwMin!)}
Time wanted  = ${formatTime(nowMin!)}
Hours from HW = ${hoursFromHw.toFixed(2)}  ${
              hoursFromHw < 0 || hoursFromHw > cyc
                ? "(outside this HW→LW cycle — extrapolating)"
                : ""
            }

Cumulative fall by twelfths (within a 6-hour cycle):
  Hour 1 → 1/12  (cum 1/12)
  Hour 2 → 2/12  (cum 3/12)
  Hour 3 → 3/12  (cum 6/12)
  Hour 4 → 3/12  (cum 9/12)
  Hour 5 → 2/12  (cum 11/12)
  Hour 6 → 1/12  (cum 12/12 = full range)

At hour ${tForRound.toFixed(2)}:
  Between cum ${cumFallen[loIdx]}/12 (hour ${loIdx})
  and       cum ${cumFallen[hiIdx]}/12 (hour ${hiIdx})
  Linear   = ${cumFallen[loIdx]} + ${frac.toFixed(2)} × ${cumFallen[hiIdx]! - cumFallen[loIdx]!}
            = ${(cumFallen[loIdx]! + frac * (cumFallen[hiIdx]! - cumFallen[loIdx]!)).toFixed(2)} /12

Fall      = ${(cumFallen[loIdx]! + frac * (cumFallen[hiIdx]! - cumFallen[loIdx]!)).toFixed(2)} /12 · ${rangeM.toFixed(2)} m
          = ${(((cumFallen[loIdx]! + frac * (cumFallen[hiIdx]! - cumFallen[loIdx]!)) / 12) * rangeM).toFixed(2)} m

Height    = ${direction === "ebb" ? "HW" : "LW"} ${direction === "ebb" ? "−" : "+"} fall
          = ${hwEff.toFixed(2)} ${direction === "ebb" ? "−" : "+"} ${(((cumFallen[loIdx]! + frac * (cumFallen[hiIdx]! - cumFallen[loIdx]!)) / 12) * rangeM).toFixed(2)}
          = ${heightNow.toFixed(2)} m`}
          </div>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter HW height, LW height, HW time + the time you want — calculator
          shows the height + the working.
        </p>
      )}
    </div>
  );
}
